import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Sparkle, Trash, Plus, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Handwash } from "@/components/Handwash";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Sign = ({ notice, testid }) => (
  <div className="notice-sign border-2 border-[#0F172A] bg-white p-6 text-center" data-testid={testid}>
    <p className="font-display text-3xl font-bold uppercase leading-tight tracking-tight text-[#B3241B] sm:text-4xl">
      {notice.heading}
    </p>
    <div className="mt-4 space-y-3">
      {(notice.lines || []).map((line, i) => (
        <p key={i} className={i === 0
          ? "font-display text-2xl font-bold uppercase leading-tight text-[#0F172A] sm:text-3xl"
          : "text-base leading-relaxed text-[#0F172A]"}>{line}</p>
      ))}
    </div>
    {notice.note && <p className="mt-5 text-sm leading-relaxed text-slate-600">{notice.note}</p>}
    {notice.easy_read && (
      <div className="mt-5 border-t border-border pt-4 text-left" data-testid={`${testid}-easy-read`}>
        <p className="overline text-[#0B5C2E]">Easy read</p>
        <p className="mt-1 text-base leading-relaxed text-[#0F172A]">{notice.easy_read}</p>
      </div>
    )}
    {notice.signed_video_url && (
      <div className="mt-4 text-left" data-testid={`${testid}-signed-video`}>
        <p className="overline text-[#002FA7]">In sign language</p>
        <video src={notice.signed_video_url} controls preload="none"
          aria-label={`${notice.heading} in sign language`} className="mt-1 w-full border border-border" />
      </div>
    )}
    <p className="mt-6 overline text-slate-400">{notice.place} · Unity &amp; Utility</p>
  </div>
);

/** A4 print view: /notices/{id}/print */
export const NoticePrint = () => {
  const { noticeId } = useParams();
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    api.get(`/facilities/public/${noticeId}`).then(({ data }) => setNotice(data)).catch(() => setNotice(null));
  }, [noticeId]);

  if (!notice) return <p className="p-10 text-center text-sm text-slate-500">Loading the sign…</p>;

  return (
    <div className="mx-auto max-w-3xl p-6 print:p-0" data-testid="notice-print-page">
      <button type="button" data-testid="notice-print-button" onClick={() => window.print()}
        className="tap-safe mb-5 border border-border px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 print:hidden">
        <Printer size={14} className="mr-1.5 inline align-[-3px]" /> Print this sign
      </button>
      <Sign notice={notice} testid="notice-print-sign" />
    </div>
  );
};

const BLANK = { place: "Restroom", heading: "", lines: ["", ""], note: "", easy_read: "", signed_video_url: "", shared: true };

export default function Notices() {
  const [board, setBoard] = useState(null);
  const [manage, setManage] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/facilities/public").then(({ data }) => setBoard(data)).catch(() => setBoard({ notices: [] }));
    api.get("/facilities/notices").then(({ data }) => setManage(data.can_manage)).catch(() => setManage(false));
  };
  useEffect(() => { load(); }, []);

  useSeo({
    title: "House notices — restroom, wash your hands and the rest | Unity & Utility",
    description: "The notices that keep a working site running: what not to flush, washing your hands properly, and any other house rule — each one a printable A4 sign.",
    path: "/notices",
    jsonLd: breadcrumbs([["Home", "/"], ["Notices", "/notices"]]),
  });

  const add = async () => {
    setBusy(true);
    try {
      await api.post("/facilities/notices", { ...form, lines: form.lines.filter((l) => l.trim()) });
      toast.success("Notice put up");
      setForm(BLANK); load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const redraw = async (id) => {
    setBusy(true);
    try { await api.post(`/facilities/notices/${id}/picture`); toast.success("Picture redrawn"); load(); }
    catch (e) { oops(e); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/facilities/notices/${id}`); toast.success("Notice taken down"); load(); }
    catch (e) { oops(e); }
  };

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="notices-page">
        <Crumbs items={[["Home", "/"], ["Notices", "/notices"]]} />
        <p className="overline text-[#B3241B]">House notices</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <Warning size={32} weight="duotone" className="mt-1 shrink-0 text-[#B3241B]" />
          The notices that keep the place running
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="notices-intro">
          Every one of these is a printable A4 sign — put it on the wall, and share it with the crew
          who never read the wall.
        </p>
        <div className="mt-5">
          <ShareBar
            url={board?.share_token ? `${window.location.origin}/p/${board.share_token}` : `${window.location.origin}/notices`}
            text={board?.notice_of_the_day ? `${board.notice_of_the_day.place} notice — ${board.notice_of_the_day.heading}` : "House notices at work"}
            testid="notices-share" instagram />
        </div>

        {board?.pinned && (
          <section className="mt-10" data-testid="notice-pinned">
            <p className="overline text-[#B3241B]">Pinned — every day, no exceptions</p>
            <div className="mt-2">
              <Sign notice={board.pinned} testid="notice-pinned-sign" />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a href={`/notices/${board.pinned.id}/print`} target="_blank" rel="noreferrer"
                data-testid="notice-pinned-print"
                className="tap-safe border border-border px-3 py-2 text-xs font-semibold text-slate-700 hover:border-[#002FA7] hover:text-[#002FA7]">
                <Printer size={13} className="mr-1 inline align-[-2px]" /> Printable sign
              </a>
              <ShareBar url={`${window.location.origin}/notices`}
                text={`${board.pinned.heading} — ${(board.pinned.lines || []).join(" ")}`}
                testid="notice-pinned-share" compact instagram />
            </div>
          </section>
        )}

        <Handwash />

        {board?.notice_of_the_day && (
          <section className="mt-10" data-testid="notice-of-the-day">
            <p className="overline text-[#002FA7]">
              Notice of the day{board.day ? ` · ${board.day}` : ""}
              {board.auto_posted && <span className="ml-2 text-[#0B5C2E]">Posted to Facebook</span>}
            </p>
            <div className="mt-2">
              <Sign notice={board.notice_of_the_day} testid="notice-of-the-day-sign" />
            </div>
          </section>
        )}

        <div className="mt-10 grid gap-5 sm:grid-cols-2" data-testid="notices-list">
          {(board?.notices || []).map((n) => (
            <article key={n.id} data-testid={`notice-${n.id}`} className="border border-border bg-white">
              {n.image_url && (
                <img src={`${BACKEND}${n.image_url}`} alt={`${n.place} notice`} loading="lazy"
                  className="h-36 w-full object-cover" />
              )}
              <div className="p-4">
                <Sign notice={n} testid={`notice-sign-${n.id}`} />
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <a href={`/notices/${n.id}/print`} target="_blank" rel="noreferrer"
                    data-testid={`notice-print-${n.id}`}
                    className="tap-safe border border-border px-3 py-2 text-xs font-semibold text-slate-700 hover:border-[#002FA7] hover:text-[#002FA7]">
                    <Printer size={13} className="mr-1 inline align-[-2px]" /> Printable sign
                  </a>
                  {manage && (
                    <>
                      <Button size="sm" variant="outline" data-testid={`notice-pin-${n.id}`}
                        onClick={async () => {
                          try { await api.post(`/facilities/notices/${n.id}/pin?pinned=${!n.pinned}`); load(); }
                          catch (e) { oops(e); }
                        }}>
                        {n.pinned ? "Unpin" : "Pin it"}
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`notice-redraw-${n.id}`}
                        onClick={() => redraw(n.id)} disabled={busy}>
                        <Sparkle size={13} className="mr-1" /> Redraw
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`notice-delete-${n.id}`}
                        onClick={() => remove(n.id)}><Trash size={13} /></Button>
                    </>
                  )}
                </div>
                <div className="mt-2">
                  <ShareBar url={`${window.location.origin}/notices`}
                    text={`${n.place} notice — ${n.heading}: ${(n.lines || []).join(" ")}`}
                    testid={`notice-share-${n.id}`} compact instagram />
                </div>
              </div>
            </article>
          ))}
        </div>

        {manage && (
          <div className="mt-10 border border-border bg-white p-4" data-testid="notice-add-form">
            <p className="overline text-slate-500">Put up a new notice</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <select data-testid="notice-place" value={form.place}
                onChange={(e) => setForm({ ...form, place: e.target.value })}
                className="tap-safe border border-border bg-white px-3 py-2 text-sm">
                {(board?.places || []).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <Input data-testid="notice-heading" placeholder="Heading (PLEASE, WASH YOUR HANDS…)"
                value={form.heading} onChange={(e) => setForm({ ...form, heading: e.target.value })} />
            </div>
            <div className="mt-2 space-y-2">
              {form.lines.map((line, i) => (
                <Input key={i} data-testid={`notice-line-${i}`} placeholder={`Line ${i + 1}`} value={line}
                  onChange={(e) => setForm({ ...form, lines: form.lines.map((l, n) => n === i ? e.target.value : l) })} />
              ))}
            </div>
            <Input className="mt-2" data-testid="notice-note" placeholder="Small print underneath (optional)"
              value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            <Input className="mt-2" data-testid="notice-easy-read"
              placeholder="Easy read version — short sentences, no jargon (optional)"
              value={form.easy_read} onChange={(e) => setForm({ ...form, easy_read: e.target.value })} />
            <Input className="mt-2" data-testid="notice-signed-video"
              placeholder="Signed video web address (optional)"
              value={form.signed_video_url} onChange={(e) => setForm({ ...form, signed_video_url: e.target.value })} />
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" data-testid="notice-line-add"
                onClick={() => setForm({ ...form, lines: [...form.lines, ""] })}>Another line</Button>
              <Button size="sm" data-testid="notice-save" onClick={add}
                disabled={busy || form.heading.trim().length < 2}>
                <Plus size={14} className="mr-1" /> Put it up
              </Button>
            </div>
          </div>
        )}
      </div>
    </PublicShell>
  );
}
